import { useEffect, useState, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Alert,
  Link,
} from '@mui/material';
import { useAuthStore } from '../../stores/root_store';
import { managerApi, type RatingItem } from '../../api/manager_api';
import { StarRating } from '../../components/StarRating';

export const ManagerRatingsPage = observer(() => {
  const authStore = useAuthStore();
  const navigate = useNavigate();

  const [ratings, setRatings] = useState<RatingItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canView = authStore.roles.includes('manager');

  const fetchRatings = useCallback(async (reset = false) => {
    setLoading(true);
    setError(null);
    try {
      const result = await managerApi.getRecentRatings(
        reset ? undefined : (cursor ?? undefined),
      );
      setRatings(prev => reset ? result.items : [...prev, ...result.items]);
      setCursor(result.cursor);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load ratings');
    } finally {
      setLoading(false);
    }
  }, [cursor]);

  useEffect(() => {
    if (canView) {
      fetchRatings(true);
    }
  }, [canView]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (!canView) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning">
          You must be a manager to view this page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" gutterBottom>
        Recent Ratings
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          {loading && ratings.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : ratings.length === 0 ? (
            <Typography color="text.secondary">No ratings found.</Typography>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Request</TableCell>
                      <TableCell>Client</TableCell>
                      <TableCell>Assistant</TableCell>
                      <TableCell>Rating</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ratings.map((item) => (
                      <TableRow key={item.uuid}>
                        <TableCell>
                          {item.request_key ? (
                            <Link
                              component="button"
                              variant="body2"
                              onClick={() => navigate(`/assistant/request/${item.request_key}`)}
                              sx={{ textAlign: 'left' }}
                            >
                              {item.request_title || 'Untitled'}
                            </Link>
                          ) : (
                            item.request_title || 'Untitled'
                          )}
                        </TableCell>
                        <TableCell>
                          {item.user ? (
                            <Link
                              component="button"
                              variant="body2"
                              onClick={() => navigate(`/manager/user/${item.user}`)}
                              sx={{ textAlign: 'left' }}
                            >
                              {item.client_name || 'Unknown'}
                            </Link>
                          ) : (
                            item.client_name || 'Unknown'
                          )}
                        </TableCell>
                        <TableCell>
                          {item.assistant ? (
                            <Link
                              component="button"
                              variant="body2"
                              onClick={() => navigate(`/manager/user/${item.assistant}`)}
                              sx={{ textAlign: 'left' }}
                            >
                              {item.assistant_name || 'Unknown'}
                            </Link>
                          ) : (
                            item.assistant_name || 'Unknown'
                          )}
                        </TableCell>
                        <TableCell>
                          <StarRating value={item.rating} compact readOnly />
                        </TableCell>
                        <TableCell>{formatDate(item.date_created)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {cursor && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Button
                    onClick={() => fetchRatings(false)}
                    disabled={loading}
                    variant="outlined"
                  >
                    {loading ? <CircularProgress size={20} /> : 'Load More'}
                  </Button>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
});
